// buildFlightMonitorRequest — pure construction of one flight-monitor refresh body.
//
// Extracted VERBATIM from `components/ManualTripCreator.js` (the per-flight body
// built inside `triggerFlightMonitors`) as part of the behavior-preserving
// refactor (issue #240). The source iterated `savedTrip.flights`, resolved each
// flight's id as `f.flightId || f.flightNumber`, SKIPPED the flight when that id
// was falsy (`if (!fid) return;`), and otherwise POSTed
// `{ userId: savedTrip.userId, tripId: savedTrip.tripId, flightId: fid }` to the
// monitor `/refresh` endpoint. This function isolates the PURE part: given the
// saved trip and one flight it returns that request body object, or `null` when
// the flight has no usable id (the caller skips `null`, matching the source's
// early `return`). The network call and iteration stay in the hook/container.
//
// Totality mirrors the source EXACTLY:
//   - `flight.flightId || flight.flightNumber` is copied verbatim; a missing id
//     yields `null` (the source's skip), not a thrown error.
//   - The body fields and their values come straight from `savedTrip`; the
//     source did not guard `savedTrip` here (it is the freshly created trip), so
//     NO extra guard is added.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * Build the monitor `/refresh` request body for one saved flight.
 *
 * @param {Object} savedTrip - The saved trip (supplies `userId` and `tripId`).
 * @param {Object} flight - The flight whose monitor should be refreshed.
 * @returns {{ userId: *, tripId: *, flightId: * }|null} The request body, or
 *          `null` when the flight has no usable id (caller skips it).
 */
export function buildFlightMonitorRequest(savedTrip, flight) {
  const fid = flight.flightId || flight.flightNumber;
  if (!fid) return null;
  return { userId: savedTrip.userId, tripId: savedTrip.tripId, flightId: fid };
}

export default buildFlightMonitorRequest;
